import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GlassCard from "@/components/GlassCard";
import CreateServerWizard from "@/components/CreateServerWizard";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Server as ServerIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  installing: "text-amber-400 bg-amber-500/10",
  pending: "text-amber-400 bg-amber-500/10",
  failed: "text-rose-400 bg-rose-500/10",
  running: "text-emerald-400 bg-emerald-500/10",
};

export default function Servers() {
  const { refresh } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const servers = useQuery({ queryKey: ["servers"], queryFn: async () => (await api.get("/servers")).data });
  const plans = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  const eggs = useQuery({ queryKey: ["eggs"], queryFn: async () => (await api.get("/eggs")).data });
  const locations = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });

  const del = async (s) => {
    if (!window.confirm(`Hapus server "${s.name}"?`)) return;
    try { await api.delete(`/servers/${s.id}`); toast.success("Server dihapus"); servers.refetch(); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-6" data-testid="servers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-white">Server Saya</h1>
          <p className="text-slate-400 mt-1">Kontrol power & kelola semua server kamu.</p>
        </div>
        <Button onClick={() => setWizardOpen(true)} className="btn-cyan text-white border-0" data-testid="servers-create-btn">
          <Plus size={16} className="mr-2" /> Buat Server
        </Button>
      </div>

      {servers.data?.length ? (
        <div className="grid lg:grid-cols-2 gap-4">
          {servers.data.map((s) => (
            <GlassCard key={s.id} className="p-5" hover data-testid={`server-row-${s.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center"><ServerIcon className="text-cyan-400" size={20} /></div>
                  <div>
                    <div className="font-heading font-bold text-white text-lg">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.egg?.name} · {s.location?.code}</div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS[s.status] || STATUS.pending}`}>{s.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.plan?.ram_gb}GB</div><div className="text-[10px] text-slate-500">RAM</div></div>
                <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.plan?.vcore}</div><div className="text-[10px] text-slate-500">vCore</div></div>
                <div className="p-2 rounded-lg bg-slate-900/50"><div className="text-sm font-semibold text-cyan-300">{s.plan?.disk_gb}GB</div><div className="text-[10px] text-slate-500">Disk</div></div>
              </div>
              {s.error && <p className="text-xs text-rose-400 mb-3 break-words">{s.error}</p>}
              <div className="flex justify-end">
                <Button size="sm" onClick={() => del(s)} className="bg-rose-600/20 border border-rose-500/30 text-rose-300 hover:bg-rose-600 hover:text-white" data-testid={`server-delete-${s.id}`}><Trash2 size={14} /></Button>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="p-10 text-center">
          <ServerIcon className="mx-auto text-slate-600 mb-3" size={40} />
          <p className="text-slate-400">Belum ada server.</p>
        </GlassCard>
      )}

      <CreateServerWizard open={wizardOpen} onOpenChange={setWizardOpen}
        plans={plans.data || []} eggs={eggs.data || []} locations={locations.data || []}
        onCreated={() => { servers.refetch(); refresh(); }} />
    </div>
  );
}
